import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Popover,
} from "@material-ui/core";
import STBoardDetail from "../../../assets/styles/organisms/boardDetail.module.scss";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import { useCallback, useContext, useMemo, useState } from "react";
import ReportIcon from "@material-ui/icons/Report";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import { LoginUserContext } from "../../../providers/LoginUserProvider";
import { useParams } from "react-router-dom";
import { PiPushPinFill } from "react-icons/pi";
import { PiPushPinSlashFill } from "react-icons/pi";
import { putReportBoardComment } from "../../../utils/api/putReportComment";
import { putPinnedStatus } from "../../../utils/api/putPinnedCommentStatus";
import { useBoard } from "../../../providers/BoardDetailProvider";
import { deleteComment } from "../../../utils/api/deleteComment";
import { useGetTabUUID } from "./hooks";
import ReportMessageBoardDialog from "../board_detail/ReportDialog";

type CommentHeaderProps = {
  id?: number;
  commentId: number;
  authorId?: string;
  title?: string;
  isPinned?: number;
  parentId?: number;
  onToggleCommentForm?: () => void;
};

const CommentActions = (props: CommentHeaderProps) => {
  const currentUser = useContext(LoginUserContext);
  const {
    state: { boardDetailData, hasPinnedComment },
    updatePinnedCommentStatus,
    reportComment,
    reportReplyComment,
    deleteComment: deleteParentComment,
    deleteReplyComment,
  } = useBoard();
  const deviceId = useGetTabUUID();
  const { board_id = 0 } = useParams<{ board_id: string }>();
  const { uid } = currentUser?.loginUser ?? {};
  const { uid: boardAuthorUid } = boardDetailData ?? {};
  const {
    authorId = "",
    title = "",
    commentId,
    isPinned = 0,
    parentId = 0,
    onToggleCommentForm,
  } = props ?? {};

  const REPORT_DIALOG = "report";
  const DELETE_DIALOG = "delete";

  const [activeDialog, setActiveDialog] = useState<string | null>(null);
  const [actionsAnchorEl, setActionsAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const onClickActionsButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const openCommentActionsPopover = Boolean(actionsAnchorEl);
  const commentActionsId = openCommentActionsPopover
    ? "comment-actions-popover"
    : undefined;

  const onCloseActionsButton = () => {
    setActionsAnchorEl(null);
  };

  const onOpenReportDialog = () => {
    setActiveDialog(REPORT_DIALOG);
  };
  const onOpenDeleteDialog = () => {
    setActiveDialog(DELETE_DIALOG);
  };

  const onCloseDialog = () => {
    setActiveDialog(null);
  };

  const handleUpdatePinnedStatus = useCallback(async () => {
    const body = {
      board_id: Number(board_id),
      comment_id: Number(commentId),
      is_pinned: Number(!isPinned),
      device_id: deviceId,
    };
    await putPinnedStatus(body).then((res) => {
      const { isSuccess } = res;
      if (!!isSuccess) {
        updatePinnedCommentStatus(commentId, !isPinned);
      }
    });
  }, [board_id, commentId, deviceId, isPinned]);

  const actions = useMemo(
    () => [
      {
        icon: <EditOutlinedIcon fontSize="inherit" />,
        title: "編集",
        onClick: onToggleCommentForm,
        rendering: uid === authorId,
      },
      {
        icon: <DeleteOutlineOutlinedIcon fontSize="inherit" />,
        title: "削除",
        onClick: onOpenDeleteDialog,
        rendering: uid === authorId,
      },
      {
        icon: <ReportIcon fontSize="inherit" />,
        title: "報告",
        onClick: onOpenReportDialog,
        rendering: uid !== authorId,
      },
      {
        icon: isPinned ? <PiPushPinSlashFill /> : <PiPushPinFill />,
        title: isPinned ? "固定を解除" : "TOPに固定",
        onClick: handleUpdatePinnedStatus,
        rendering:
          boardAuthorUid === uid &&
          !parentId &&
          (!hasPinnedComment || isPinned),
      },
    ],
    [
      authorId,
      boardAuthorUid,
      handleUpdatePinnedStatus,
      hasPinnedComment,
      isPinned,
      onToggleCommentForm,
      parentId,
      uid,
    ]
  );

  const handleReport = async (reason: string) => {
    const body = {
      id: commentId,
      board_id: Number(board_id),
      device_id: deviceId,
      reason,
    };
    await putReportBoardComment(body).then((res: any) => {
      if (res.isSuccess) {
        onCloseDialog();
        const response = res?.result ?? {};
        const commentFlags = response?.comment_flags ?? {};
        let checkCommentFlag = 0;
        if (uid && Object.hasOwn(commentFlags, uid)) {
          checkCommentFlag = commentFlags[uid];
        }
        if (parentId) {
          reportReplyComment(parentId, commentId, checkCommentFlag);
        } else {
          const countReportedComment = response?.count_reported_items || 1;
          reportComment(commentId, countReportedComment, checkCommentFlag);
        }
      }
    });
  };

  const handleDelete = async () => {
    await deleteComment(commentId, deviceId).then((res: any) => {
      if (res.isSuccess) {
        onCloseDialog();
        const response = res?.result ?? {};
        const countDeletedItems = response?.count_deleted_items || 1;
        const commentFlags = response?.comment_flags ?? {};
        let checkCommentFlag = 0;
        if (uid && Object.hasOwn(commentFlags, uid)) {
          checkCommentFlag = commentFlags[uid];
        }
        if (!parentId) {
          deleteParentComment(commentId, countDeletedItems, checkCommentFlag);
        } else {
          deleteReplyComment(parentId, commentId, checkCommentFlag);
        }
      }
    });
  };

  return (
    <>
      <div>
        <Button
          aria-describedby={commentActionsId}
          onClick={onClickActionsButton}
        >
          <MoreHorizIcon />
        </Button>
        <Popover
          id={commentActionsId}
          open={openCommentActionsPopover}
          anchorEl={actionsAnchorEl}
          onClose={onCloseActionsButton}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <div onClick={onCloseActionsButton}>
            {actions.map((action) => {
              const { icon, title, onClick, rendering = false } = action;
              if (!rendering) {
                return null;
              }
              return (
                <div
                  key={title}
                  onClick={onClick}
                  className={STBoardDetail.actionContainer}
                >
                  {icon} <p>{title}</p>
                </div>
              );
            })}
          </div>
        </Popover>
      </div>
      <ReportMessageBoardDialog
        isOpen={activeDialog === REPORT_DIALOG}
        onClose={onCloseDialog}
        handleReport={handleReport}
      />
      <Dialog
        open={activeDialog === DELETE_DIALOG}
        onClose={onCloseDialog}
        aria-labelledby="alert-dialog_delete-title"
        aria-describedby="alert-dialog-delete-description"
        className={STBoardDetail.dialog}
      >
        <DialogContent>
          <DialogTitle
            className={STBoardDetail.dialogTitle}
            id="alert-dialog_delete-title"
          >
            {title}
          </DialogTitle>
          <DialogContentText
            id="alert-dialog-delete-description"
            className={STBoardDetail.dialogDescription}
          >
            {`このコメントは削除されます。\n※ 一度削除すると元に戻せません。よろしいですか？`}
          </DialogContentText>
        </DialogContent>
        <DialogActions
          style={{
            display: "flex",
            justifyContent: "center",
          }}
          className={STBoardDetail.buttonGroup}
        >
          <Button className={STBoardDetail.backButton} onClick={onCloseDialog}>
            戻る
          </Button>
          <Button className={STBoardDetail.submitButton} onClick={handleDelete}>
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentActions;
